import { MapOutlined } from '@mui/icons-material';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { selectImpactCalculateTourForCurrentProject } from '@/domain/impact-calculate-tour/impact-calculate-tour.selectors';
import { buildMapUrl } from '@/domain/position-geocode/utils/osm/build-osm-map-url';
import { TourEvent } from '@/domain/project-tour-ontour/entities/project-tour-ontour-event.entity';
import { ScoreText } from '@/ui/components/score-text/ScoreText';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { ColorsRaw } from '@/ui/theme/colors-raw';
import { Fonts } from '@/ui/theme/fonts';
import { formatCo2Value } from '@/ui/utils/format-co2-value';
import { formatDate } from '@/ui/utils/format-date';
import { truncateWithEllipsis } from '@/ui/utils/truncate-text';

import { ActionMenuForTourEventItem } from './ActionMenuForTourEventItem';
import { ModalTourEventForm } from './tour-event-view/ModalTourEventForm';

export function TourEventItem({
  tourEvent,
  onClickDelete,
}: {
  tourEvent: TourEvent;
  onClickDelete: (tourEventId: number) => void;
}) {
  const [modalOpen, setModalOpen] = useState(false);

  const calculateTour = useAppSelector(
    selectImpactCalculateTourForCurrentProject,
  );
  const co2TourEvent =
    calculateTour?.co2?.ontour?.byTourEvent[tourEvent.id]?.score || 0;

  const onClickOpen = () => {
    setModalOpen(true);
  };
  const onCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <>
      <ModalTourEventForm
        open={modalOpen}
        onClose={onCloseModal}
        tourEvent={tourEvent}
      />
      <Box
        sx={{
          width: '21rem',
          backgroundColor: ColorsRaw.anthracite.Warm_Anthracite_2,
          borderRadius: 2,
          paddingY: 1,
          paddingX: 1,
        }}
      >
        <Stack
          sx={{
            flexDirection: 'column',
            gap: 0,
          }}
        >
          {/* line 1 */}
          <Stack
            sx={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <TourEventName tourEvent={tourEvent} />
            <ScoreText
              value={`${formatCo2Value(co2TourEvent)} ${translate('Projects.teCO2')}`}
              variant={'box'}
              boxTextColor={ColorsRaw.secondary.Light_Grey}
              boxBackgroundColor={ColorsRaw.anthracite.Anthracite_4}
            />
            <ActionMenuForTourEventItem
              onClickEdit={onClickOpen}
              onClickDelete={() => onClickDelete(tourEvent.id)}
            />
          </Stack>
          {/* line 2 */}
          <Stack
            sx={{
              flexDirection: 'row',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <TourEventDate tourEvent={tourEvent} />
            <TourEventPosition tourEvent={tourEvent} />
          </Stack>
          {/* line 3 */}
          <Stack>
            <TouEventTypeAndCapacity tourEvent={tourEvent} />
          </Stack>
        </Stack>
      </Box>
    </>
  );
}

function TourEventName({ tourEvent }: { tourEvent: TourEvent }) {
  return (
    <Typography
      sx={{
        flexGrow: 1,
        fontSize: '1.25rem',
        fontWeight: 800,
        fontFamily: Fonts.DarkerGrotesque,
        color: '#FFEFE6',
      }}
    >
      {tourEvent.name}
    </Typography>
  );
}

function TourEventDate({ tourEvent }: { tourEvent: TourEvent }) {
  return (
    <Typography
      sx={{
        fontSize: '0.875rem',
        fontWeight: 600,
        opacity: 0.7,
      }}
    >
      {formatDate(tourEvent.date)}
    </Typography>
  );
}

function TourEventPosition({ tourEvent }: { tourEvent: TourEvent }) {
  return (
    <>
      {tourEvent.position && (
        <Stack sx={{ flexDirection: 'row', gap: 1, alignItems: 'center' }}>
          <IconButton
            size="small"
            href={buildMapUrl(tourEvent.position.coordinates)}
            target="_blank"
          >
            <MapOutlined fontSize="small" />
          </IconButton>
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 600,
              opacity: 0.7,
            }}
          >
            {truncateWithEllipsis(tourEvent.position.properties.name, 30)}
          </Typography>
        </Stack>
      )}
    </>
  );
}

function TouEventTypeAndCapacity({ tourEvent }: { tourEvent: TourEvent }) {
  return (
    <Typography
      sx={{
        fontSize: '0.875rem',
        fontWeight: 600,
        opacity: 0.7,
      }}
    >
      {translate(`TourEventTypes.${tourEvent.type}`)} {' - '}
      {translate('Projects.X-persons', { count: tourEvent.capacity })}
    </Typography>
  );
}
